import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@clay-run/glaze/button';

import { useFlowSessionStore } from '../flow-session-store';
import { discardAllUnsavedWork, useHasUnsavedWork } from './UnsavedWorkGuard';

/**
 * Round-4 U7 (E3) — the header's standing reminder that a flow the rep
 * walked away from still holds unsaved edits, and the one-click way back.
 *
 * This is the visible half of "Back never loses work". Because
 * `PlayFlowShell` no longer wipes the draft stores on unmount, a rep who
 * hits Back mid-edit has drafts sitting in module scope with nothing on
 * screen saying so — which would be a WORSE failure than losing them
 * (silently stale work that reappears later). The pill closes that: while
 * any unsaved work exists and the rep is somewhere other than the flow it
 * belongs to, the header says so and links straight back to the exact
 * `?step=` they left from (`flow-session-store`'s `returnHref`).
 *
 * Hidden on the flow itself — `returnHref` equal to the current location
 * means the work is right there in front of the rep and a pill pointing at
 * the current page would be noise.
 */
export function UnsavedEditsPill() {
  const hasUnsavedWork = useHasUnsavedWork();
  const returnHref = useFlowSessionStore((state) => state.returnHref);
  const label = useFlowSessionStore((state) => state.label);
  const location = useLocation();
  const navigate = useNavigate();

  if (!hasUnsavedWork || returnHref === null || label === null) return null;
  if (`${location.pathname}${location.search}` === returnHref) return null;

  return (
    <div className="flex items-center gap-1">
      <Button variant="secondary" size="sm" onClick={() => navigate(returnHref)}>
        Unsaved edits · {label}
      </Button>
      <Button
        variant="ghost"
        size="sm"
        aria-label="Discard unsaved edits"
        // Codex review of Playbooks QA round 5 (finding B1 follow-up) —
        // `discardAllUnsavedWork` now takes an optional `scope` param;
        // passing the bare function reference as `onClick` would hand it
        // React's SyntheticEvent as `scope` instead of `undefined`, silently
        // turning this global "discard the whole flow" button into a no-op
        // scoped clear that matches nothing. This button is always the
        // unscoped Play-flow discard (see this component's own module doc),
        // so it must call with no arguments.
        onClick={() => discardAllUnsavedWork()}
      >
        Discard
      </Button>
    </div>
  );
}
